import ace from 'brace';
import 'brace/ext/language_tools';

import {MeasureUtil} from 'common/util';
import mpFonts from 'common/stylesheets/mixins/fonts.json';

import css from './patches.inline.styl';

const {Tokenizer} = ace.acequire(`ace/tokenizer`);
Tokenizer.prototype.$setMaxTokenCount(100000);

// Override popup settings here because many autocomplete popup measurements are in JS instead of CSS
const {Autocomplete} = ace.acequire(`ace/autocomplete`);
const oldAutoCompleteInit = Autocomplete.prototype.$init;
(function () {
  this.$init = function () {
    const popup = oldAutoCompleteInit.call(this);
    popup.renderer.$maxLines = 8; // this is the default, but worth declaring here as a notable option
    popup.container.style.lineHeight = 2.6; // 1x lineHeight seems to be ~11.6px (???)
    popup.renderer.setPadding(16); // horizontal padding
    popup.renderer.setScrollMargin(8, 8, 0, 0); // vertical padding; argument order is "top, bottom, left, right"

    popup.session.bgTokenizer._cachedMetaWidths = {};
    popup.session.bgTokenizer._measureMetaWidth = function (text) {
      if (!(text in popup.session.bgTokenizer._cachedMetaWidths)) {
        const font = `500 12px ${mpFonts.fontFamilyPrimary}`;
        popup.session.bgTokenizer._cachedMetaWidths[text] = MeasureUtil.measureTextWidth(text, font);
      }
      return popup.session.bgTokenizer._cachedMetaWidths[text];
    };

    /**
     * $tokenizeRow() is called by the Ace editor
     *
     * This function breaks up autocomplete results into tokens for styling and formatting.
     * Each token is structured with its own div in the DOM when the autocomplete menu is rendered
     * and each div's class contains token.type (it looks like: class="ace_customtokentype").
     *
     * Primary purpose of overriding this method is to apply custom autocomplete abbreviation.
     * Ace's default autocomplete abbreviation behaviour is to truncate the end of a result's meta tag
     * while the following custom behaviour is to truncate the result's caption while preserving the meta tag.
     *
     * Much of the truncation code is adapted from the "ace" project which is BSD licensed https://github.com/ajaxorg/ace/blob/master/LICENSE
     */
    popup.session.bgTokenizer.$tokenizeRow = function (row) {
      const tokens = [];
      const data = popup.data[row];
      if (data) {
        const captionWidth =
          popup.renderer.layerConfig.width -
          (data.meta ? popup.session.bgTokenizer._measureMetaWidth(data.meta) + 8 : 0);
        const maxLength = Math.floor(captionWidth / popup.renderer.layerConfig.characterWidth);
        const caption = typeof data === `string` ? data : data.caption || data.value || data.name;
        const captionClass = data.className || ``;
        const shouldTruncate = caption.length > maxLength;

        let last = -1;
        let flag, c;
        for (let i = 0; i < Math.min(caption.length, maxLength - (shouldTruncate ? 1 : 0)); i++) {
          c = caption[i];
          flag = data.matchMask & (1 << i) ? 1 : 0;
          if (last !== flag) {
            tokens.push({type: captionClass + (flag ? `completion-highlight` : ``), value: c});
            last = flag;
          } else {
            tokens[tokens.length - 1].value += c;
          }
        }

        if (shouldTruncate) {
          const lastToken = tokens[tokens.length - 1];
          if (lastToken && lastToken.value.length > 1) {
            lastToken.value = lastToken.value.slice(0, -1) + `\u2026`; // \u2026 = ellipsis
          } else if (tokens.length > 0) {
            tokens.pop();
            tokens.push({type: captionClass, value: `\u2026`});
          }
        }

        if (data.meta) {
          tokens.push({type: `rightAlignedText`, value: data.meta});
        }
      }

      return tokens;
    };
    return popup;
  };
}).call(Autocomplete.prototype);

// Override live autocomplete option in ace editor
// Primary purpose of overriding is to broaden autocomplete triggers
// Ace's built-in autocomplete is designed for keyword completion
// so it typically triggers on the first letter of space-separated words
// but here we add additional triggers for `.` and string-literal quotes to better support Javascript syntax completion
// Much of this override code is adapted from the "ace" project which is BSD licensed https://github.com/ajaxorg/ace/blob/master/LICENSE
const util = ace.acequire(`ace/autocomplete/util`);
const aceConfig = ace.acequire(`ace/config`);
const doLiveAutocomplete = function (ev) {
  const editor = ev.editor;
  const hasCompleter = editor.completer && editor.completer.activated;
  if (ev.command.name === `backspace`) {
    if (hasCompleter && !util.getCompletionPrefix(editor)) editor.completer.detach();
  } else if (ev.command.name === `insertstring`) {
    const cursor = ev.editor.getSelectionRange().end;
    const token = editor.session.getTokenAt(cursor.row, cursor.column);
    const prefix = util.getCompletionPrefix(editor);
    if (!editor.completer) {
      editor.completer = new Autocomplete();
    }
    const tokenType = token && token.type;
    const isEscapedQuote = tokenType === `constant.language.escape` && (ev.args === `"` || ev.args === `'`);
    if (
      (ev.args === `.` || tokenType === `string` || tokenType === `string.quoted` || isEscapedQuote) &&
      !hasCompleter
    ) {
      editor.execCommand(`startAutocomplete`);
    } else if (prefix && !hasCompleter) {
      editor.completer.autoInsert = false;
      editor.completer.showPopup(editor);
    }
  }
};

aceConfig.defineOptions(ace.acequire(`ace/editor`).Editor.prototype, `editor`, {
  enableLiveAutocompletion: {
    set(val) {
      if (val) {
        if (!this.completers) {
          if (!Array.isArray(val)) {
            throw new Error(`enableLiveAutocompletion setting value must be an array of completers`);
          }
          this.completers = val;
        }
        this.commands.addCommand(Autocomplete.startCommand);
        this.commands.on(`afterExec`, doLiveAutocomplete);
      } else {
        this.commands.removeCommand(Autocomplete.startCommand);
        this.commands.removeListener(`afterExec`, doLiveAutocomplete);
      }
    },
    value: false,
  },
});

ace.define(
  `ace/mode/arb-selector_highlight_rules`,
  [`ace/lib/oop`, `ace/mode/text_highlight_rules`],
  (acequire, exports, _module) => {
    const oop = acequire(`../lib/oop`);
    const TextHighlightRules = acequire(`./text_highlight_rules`).TextHighlightRules;

    const encodedPropertyRegex = /@"""("(?!"")|[^"])+"""@/;
    const ArbSelectorHighlightRules = function () {
      this.$rules = {
        start: [
          {
            token: `keyword.operator`,
            regex: /([iI][nN]|[oO][rR]|[aA][nN][dD]|[nN][oO][tT])(?![a-zA-Z0-9])/,
          },
          {
            token: `constant.numeric`,
            regex: /[0-9]+(\.[0-9]+)?/,
          },
          {
            token: `constant.language`,
            regex: /([tT][rR][uU][eE]|[fF][aA][lL][sS][eE]|[uU][nN][dD][eE][fF][iI][nN][eE][dD])(?![a-zA-Z0-9])/,
          },
          {
            // encoded_properties are not actually part of the selector language, but when an
            // encoded_property token is encountered the custom property formula text box has
            // special logic to replace it with a query-block that lets you select a property
            token: `encoded_property`,
            regex: encodedPropertyRegex,
            merge: false, // stops neighboring encoded properties from becoming a single token
          },
          {
            token: `string.quoted`,
            regex: /"/,
            next: `string`,
          },
          {
            token: `entity.name.function`,
            regex: /[a-zA-Z_]+(?=\s*\()/,
          },
          {
            token: `variable.other`,
            regex: /[a-zA-Z_]+(?!\s*\()/,
          },
          {
            token: `comment`,
            regex: /\/\//,
            next: `single_line_comment`,
          },
          {
            token: `comment`,
            regex: /\/\*/,
            next: `multi_line_comment`,
          },
        ],
        single_line_comment: [
          {
            // encoded properties within comments are treated as syntax errors, but are still
            // rendered as property pills
            token: `comment.encoded_property`,
            regex: encodedPropertyRegex,
            merge: false, // stops neighboring encoded properties from becoming a single token
          },
          {
            token: `comment`,
            regex: /((?!@""")[^\n])+/,
          },
          {
            regex: /$/,
            next: `start`,
          },
        ],
        multi_line_comment: [
          {
            token: `comment`,
            regex: /\*\//,
            next: `start`,
          },
          {
            // encoded properties within comments are treated as syntax errors, but are still
            // rendered as property pills
            token: `comment.encoded_property`,
            regex: encodedPropertyRegex,
            merge: false,
          },
          {
            token: `comment`,
            regex: /(\*(?!\/)|(?!@""")[^*])+/,
          },
        ],
        string: [
          {
            token: `string.quoted`,
            regex: /"/,
            next: `start`,
          },
          {
            // encoded properties within strings are treated as syntax errors, but are still
            // rendered as property pills
            token: `string.encoded_property`,
            regex: encodedPropertyRegex,
            merge: false,
          },
          {
            token: `string.quoted`,
            regex: /((\\")|\\|(?!@""")[^"\\\n])+/,
          },
          {
            regex: /$/,
            next: `start`,
          },
        ],
      };
    };

    oop.inherits(ArbSelectorHighlightRules, TextHighlightRules);
    ArbSelectorHighlightRules.metaData = {
      scopeName: `source.arb-selector`,
    };
    exports.ArbSelectorHighlightRules = ArbSelectorHighlightRules;
  },
);

ace.define(`ace/mode/arb-selector`, [], (acequire, exports, _module) => {
  const oop = acequire(`../lib/oop`);
  const TextMode = acequire(`./text`).Mode;
  const ArbSelectorHighlightRules = acequire(`./arb-selector_highlight_rules`).ArbSelectorHighlightRules;

  const Mode = function () {
    this.HighlightRules = ArbSelectorHighlightRules;
    this.$behaviour = this.$defaultBehaviour;
  };
  oop.inherits(Mode, TextMode);

  (function () {
    this.lineCommentStart = null;
    this.blockComment = null;
    this.$id = `ace/mode/arb-selector`;
  }).call(Mode.prototype);

  exports.Mode = Mode;
});

// Ace attaches it's autocomplete menu to `document.head` so in order to have our styles applied
// to it we need to inject them into `document.head` as well.
window.addEventListener(
  `load`,
  function () {
    const elementId = `iron-common-util-ace-patches`;
    if (!document.head.querySelector(`script#${elementId}`)) {
      const injectedStyle = document.createElement(`style`);
      injectedStyle.setAttribute(`id`, elementId);
      injectedStyle.textContent = css;
      document.head.appendChild(injectedStyle);
    }
  },
  false,
);
